import { IonAlert, IonButton, IonContent, IonHeader, IonInput, IonPage, useIonViewWillEnter } from "@ionic/react";
import { RouteComponentProps } from "react-router";
import Footer from "../components/Footer";
import Toolbar from "../components/Toolbar";
import "./GroupManagement.css";
import "../theme/GlobalStyles.css";
import RouteText from "../components/RouteText";
import { useContext, useState } from "react";
import EditGroupModal from "../components/EditGroupModal";
import { Group } from "../utils/Interfaces";
import AppContext from "../providers/AppContext";
import CustomTableWithEditDeleteCol from "../components/CustomTableWithEditDeleteCol";
import { TABLE_COLUMNS_GROUPS } from "../utils/Columns";
import ResultsText from "../components/ResultsText";
import { APIService } from "../network/APIService";
import DeleteGroupModal from "../components/DeleteGroupModal";
import TableInstructionText from "../components/TableInstructionText";
import ReloadDataBtn from "../components/ReloadDataBtn";

const GroupManagement: React.FC<RouteComponentProps> = props => {
  const appContext = useContext(AppContext);

  const [allGroups, setAllGroups] = useState<Group[]>([]);
  const [filteredGroups, setFilteredGroups] = useState<Group[]>([]);
  const [showGetGroupsFailedModal, setShowGetGroupsFailedModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group>();
  const [searchText, setSearchText] = useState("");
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);

  useIonViewWillEnter(() => {
    if (appContext.jwt === "") {
      // browser refreshed so move back to landing
      props.history.replace("/");
    } else {
      // browser not refreshed so get the data
      getGroups();
    }
  }, []);

  const getGroups = async () => {
    const groups = await APIService.getGroups(appContext.jwt);
    if (groups) {
      // successfully got groups
      setAllGroups(groups);
      setFilteredGroups(groups);
    } else {
      // failed to get groups
      setShowGetGroupsFailedModal(true);
    }
  };

  function handleSearch(searchText: string) {
    setSearchText(searchText);

    let filteredList = [];
    const text = searchText.toLowerCase().trim();

    if (!text || text === "") {
      // reapply filters without search text
      filteredList = allGroups;
    } else {
      // filter list
      filteredList = allGroups.filter((value, index, array) => {
        const filterArray = [value.id.toString(), value.name.toLowerCase()];
        return filterArray.some(value => value.includes(text));
      });
    }

    setFilteredGroups(filteredList);
  }

  return (
    <IonPage>
      <IonHeader>
        <Toolbar props={props} />
      </IonHeader>
      <IonContent>
        <div className="pageContainer">
          <div className="pageContentContainer">
            <RouteText
              routes={[{ name: "Dashboard", route: "/" }]}
              currentLocation="Group Management"
              routeProps={props}
            />
            <h1 className="pageHeader">Groups</h1>
            <div id="searchGroupsContainer">
              <p id="searchBarTitle">Search</p>
              <IonInput value={searchText} onIonChange={e => handleSearch(e.detail.value!)} />
            </div>
            <div id="addNewGroupContainer">
              <ResultsText count={filteredGroups.length} />
              <div>
                <ReloadDataBtn handleClick={getGroups} />
                <IonButton className="yellowBtn" onClick={() => setShowAddModal(true)}>
                  + Add new group
                </IonButton>
              </div>
            </div>
            <CustomTableWithEditDeleteCol
              cols={TABLE_COLUMNS_GROUPS}
              rowData={filteredGroups}
              handleEditItem={item => {
                setSelectedGroup(item);
                setShowEditModal(true);
              }}
              handleDeleteItem={item => {
                setSelectedGroup(item);
                setShowDeleteGroupModal(true);
              }}
            />
            <TableInstructionText text="Search input looks for matches to id or name." />
            {showEditModal && (
              <EditGroupModal
                modalTitle="Edit Group"
                selectedGroup={selectedGroup}
                handleModalDismiss={() => {
                  setShowEditModal(false);
                  getGroups();
                }}
              />
            )}
            {showAddModal && (
              <EditGroupModal
                modalTitle="Add New Group"
                handleModalDismiss={() => {
                  setShowAddModal(false);
                  getGroups();
                }}
              />
            )}
            <DeleteGroupModal
              group={selectedGroup}
              isOpen={showDeleteGroupModal}
              handleModalDismiss={deletedGroup => {
                getGroups();
                setShowDeleteGroupModal(false);
              }}
            />
          </div>
          <Footer />
        </div>
        <IonAlert
          isOpen={showGetGroupsFailedModal}
          onDidDismiss={() => setShowGetGroupsFailedModal(false)}
          header={"Alert"}
          message={"Failed to get groups. Please try again later."}
          buttons={["OK"]}
        />
      </IonContent>
    </IonPage>
  );
};

export default GroupManagement;
